import { useMemo, useState } from "react"
import { Bar, BarChart, CartesianGrid, Cell, XAxis, YAxis } from "recharts"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@components/common/card"
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from "@components/common/chart"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@components/common/table"
import { Button } from "@components/common/button"
import { cn, exportTableToExcel } from "@pma/utils/helpers"
import { ChevronDown, Download } from "lucide-react"
import { StatusBadge } from "../widgets"
import { resolveEntityStatus } from "../analytics-engine"
import type { ProgressRow } from "./types"
import { CURRENT_COLUMN_NAME, INITIAL_COLUMN_NAME, KNOWN_CURRENT_COLUMN_IDS, KNOWN_INITIAL_COLUMN_IDS } from "@pma/utils/constants"
import { DictStatus } from "@pma/types"

function findCustomColumnValue(
  customColumns: Array<{ column_id: number | string; value: string | number | null }> | undefined,
  knownIds: number[],
  columnName: string,
): number | null {
  if (!customColumns || !Array.isArray(customColumns)) return null
  for (const id of knownIds) {
    const match = customColumns.find((c) => Number(c.column_id) === id)
    if (match && match.value != null && match.value !== "") return Number(match.value)
  }
  const byName = customColumns.find(
    (c: any) => c.name === columnName || c.column_name === columnName,
  )
  if (byName && byName.value != null && byName.value !== "") return Number(byName.value)
  return null
}

export function TaskProgressTable({
  filteredTasks,
  statuses,
  projectsListRaw,
  translate,
  isHebrew,
}: {
  filteredTasks: any[]
  statuses: DictStatus[]
  projectsListRaw: any[]
  translate: (key: string) => string
  isHebrew: boolean
}) {
  const projectNameMap = useMemo(() => {
    const map = new Map<number, string>()
    ;(projectsListRaw).forEach((p: any) => {
      if (p?.id) map.set(p.id, p.title || p.name || "-")
    })
    return map
  }, [projectsListRaw])

  const rows: ProgressRow[] = useMemo(() => {
    return filteredTasks.map((task: any) => {
      const display = resolveEntityStatus(task, statuses)
      const pid = task?.project_id || task?._project?.id
      const projectName =
        task?._project?.title || task?._project?.name || projectNameMap.get(pid) || "-"
      const initial = findCustomColumnValue(
        task?.custom_columns,
        KNOWN_INITIAL_COLUMN_IDS,
        INITIAL_COLUMN_NAME,
      )
      const current = findCustomColumnValue(
        task?.custom_columns,
        KNOWN_CURRENT_COLUMN_IDS,
        CURRENT_COLUMN_NAME,
      )
      const gap = initial != null && current != null ? initial - current : null
      const gapPct =
        initial != null && initial > 0 && current != null
          ? Math.round((current / initial) * 100)
          : 0
      return {
        projectName,
        taskName: task?.title || task?.name || "-",
        statusName: display.name,
        statusColor: display.color,
        initial,
        current,
        gap,
        gapPct,
      }
    })
  }, [filteredTasks, statuses, projectNameMap])

  const chartData = useMemo(() => {
    return rows
      .filter((r) => r.initial != null && r.initial > 0)
      .map((r) => ({
        name: r.taskName,
        gapPct: r.gapPct,
        color: r.gapPct >= 80 ? "#22c55e" : r.gapPct >= 50 ? "#eab308" : "#ef4444",
      }))
  }, [rows])

  const progressChartConfig = useMemo(
    () =>
      ({
        gapPct: { label: translate("Gap %"), color: "#3b82f6" },
      }) satisfies ChartConfig,
    [translate],
  )

  const [progressTableOpen, setProgressTableOpen] = useState(true)
  const [progressChartOpen, setProgressChartOpen] = useState(true)

  if (!filteredTasks.length) return null

  return (
    <>
      <Card className='overflow-hidden border border-gray-100 shadow-sm transition-shadow duration-200 hover:shadow-md'>
        <CardHeader
          className='cursor-pointer select-none px-5 pb-3 pt-5'
          onClick={() => setProgressTableOpen(!progressTableOpen)}
        >
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-2'>
              <ChevronDown
                className={cn(
                  "h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200",
                  !progressTableOpen && "-rotate-90",
                )}
              />
              <div>
                <CardTitle className='text-sm font-semibold tracking-tight text-foreground'>
                  {"סטטוס משימות פר פרויקט"}
                </CardTitle>
                <CardDescription className='text-xs'>
                  {translate("Task Status per Project")}
                  {" — "}
                  {rows.length} {translate("Tasks")}
                </CardDescription>
              </div>
            </div>
            <Button
              variant='ghost'
              size='icon'
              className='h-8 w-8'
              title={translate("Export")}
              onClick={() => {
                const exportRows = rows.map((r) => ({
                  [translate("project name")]: r.projectName,
                  [translate("Task")]: r.taskName,
                  [translate("status")]: r.statusName,
                  [INITIAL_COLUMN_NAME]: r.initial ?? "-",
                  [CURRENT_COLUMN_NAME]: r.current ?? "-",
                  ["הפער"]: r.gap ?? "-",
                  ["הפער ב-%"]: r.initial != null || r.current != null ? `${r.gapPct}%` : "-",
                }))
                exportTableToExcel(exportRows, "task-progress")
              }}
            >
              <Download className='h-4 w-4' />
            </Button>
          </div>
        </CardHeader>
        {progressTableOpen && (
          <CardContent className='px-0 pb-3'>
            <div className='overflow-x-auto'>
              <Table>
                <TableHeader>
                  <TableRow className='hover:bg-transparent'>
                    <TableHead className='px-5 text-start'>{translate("project name")}</TableHead>
                    <TableHead className='text-start'>{translate("Task")}</TableHead>
                    <TableHead className='text-start'>{translate("status")}</TableHead>
                    <TableHead className='text-center'>{INITIAL_COLUMN_NAME}</TableHead>
                    <TableHead className='text-center'>{CURRENT_COLUMN_NAME}</TableHead>
                    <TableHead className='text-center'>{"הפער"}</TableHead>
                    <TableHead className='text-center'>{"הפער ב-%"}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row, idx) => (
                    <TableRow key={idx} className='transition-colors hover:bg-gray-50/80'>
                      <TableCell className='max-w-[180px] truncate px-5 text-sm font-medium'>
                        {row.projectName}
                      </TableCell>
                      <TableCell className='max-w-[200px] truncate text-sm'>
                        {row.taskName}
                      </TableCell>
                      <TableCell>
                        <StatusBadge
                          statusDisplay={{ name: row.statusName, color: row.statusColor }}
                        />
                      </TableCell>
                      <TableCell className='text-center font-mono text-sm tabular-nums'>
                        {row.initial ?? "-"}
                      </TableCell>
                      <TableCell className='text-center font-mono text-sm tabular-nums'>
                        {row.current ?? "-"}
                      </TableCell>
                      <TableCell className='text-center font-mono text-sm tabular-nums'>
                        {row.gap ?? "-"}
                      </TableCell>
                      <TableCell className='text-center font-mono text-sm tabular-nums'>
                        {row.initial != null || row.current != null ? (
                          <span
                            className={cn(
                              "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold",
                              row.gapPct >= 80
                                ? "bg-green-100 text-green-800"
                                : row.gapPct >= 50
                                  ? "bg-yellow-100 text-yellow-800"
                                  : "bg-red-100 text-red-800",
                            )}
                          >
                            {row.gapPct}%
                          </span>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        )}
      </Card>

      {chartData.length > 0 && (
        <Card className='overflow-hidden border border-gray-100 shadow-sm transition-shadow duration-200 hover:shadow-md'>
          <CardHeader
            className='cursor-pointer select-none px-5 pb-3 pt-5'
            onClick={() => setProgressChartOpen(!progressChartOpen)}
          >
            <div className='flex items-center gap-2'>
              <ChevronDown
                className={cn(
                  "h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200",
                  !progressChartOpen && "-rotate-90",
                )}
              />
              <div>
                <CardTitle className='text-sm font-semibold tracking-tight text-foreground'>
                  {"הפער ב-% לפי משימה"}
                </CardTitle>
                <CardDescription className='text-xs'>
                  {translate("Gap percentage per task")}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          {progressChartOpen && (
            <CardContent>
              <ChartContainer
                config={progressChartConfig}
                className='w-full'
                style={{ height: Math.max(200, chartData.length * 48), direction: "ltr" }}
              >
                <BarChart
                  layout='vertical'
                  data={chartData}
                  margin={{ top: 4, right: isHebrew ? 8 : 24, left: isHebrew ? 24 : 8, bottom: 4 }}
                >
                  <CartesianGrid horizontal={false} strokeDasharray='3 3' stroke='#f0f0f0' />
                  <XAxis
                    type='number'
                    domain={[0, 100]}
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 11, fill: "#6b7280" }}
                    tickFormatter={(v: number) => `${v}%`}
                  />
                  <YAxis
                    type='category'
                    dataKey='name'
                    width={220}
                    tickLine={false}
                    axisLine={false}
                    orientation={isHebrew ? "right" : "left"}
                    tick={{ fontSize: 12, fill: "#374151", textAnchor: isHebrew ? "start" : "end" }}
                  />
                  <ChartTooltip cursor={false} content={<ChartTooltipContent />} />
                  <Bar dataKey='gapPct' radius={[0, 4, 4, 0]} maxBarSize={28}>
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ChartContainer>
            </CardContent>
          )}
        </Card>
      )}
    </>
  )
}
